import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
	Typography,
	Button,
	TextField,
	MenuItem,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Grow,
	Fade
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import clsx from 'clsx';
import JString from '@easytech-international-sdn-bhd/jstring';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../Services/API_Service';
import { req_promo_campaign, req_promo_campaign_delete, req_stock_brand } from '../../Helper/Constants';
import LinearLoading from '../../Components/LinearLoading';
import PromoCard from './components/PromoCard';
import PromoFormDialog from './components/PromoFormDialog';
import { getCampaignStatus, monthOverlaps, buildMonthOptions } from './helpers';

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
	return <Grow ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: theme.spacing(0.5)
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5)
	},
	titleWrap: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		marginRight: 'auto'
	},
	title: {
		fontWeight: 700,
		letterSpacing: '-0.01em'
	},
	countBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 26,
		height: 22,
		padding: theme.spacing(0, 0.75),
		borderRadius: 11,
		fontSize: 12,
		fontWeight: 700,
		fontVariantNumeric: 'tabular-nums',
		color: theme.palette.text.secondary,
		backgroundColor: fade(theme.palette.text.primary, 0.06)
	},
	filterSelect: {
		width: 170,
		'& .MuiOutlinedInput-root': {
			borderRadius: 10,
			backgroundColor: theme.palette.background.paper
		}
	},
	newBtn: {
		backgroundColor: '#FEF200',
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		borderRadius: 10,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		boxShadow: `0 1px 2px ${fade('#000', 0.2)}`,
		transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], { duration: 180 }),
		'&:hover': {
			backgroundColor: '#FFF53D',
			transform: 'translateY(-1px)',
			boxShadow: `0 6px 16px ${fade('#FEF200', 0.5)}`
		},
		'&:active': {
			transform: 'translateY(0)'
		}
	},
	pillRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.75),
		marginBottom: theme.spacing(1.5)
	},
	pill: {
		height: 28,
		padding: theme.spacing(0, 1.5),
		borderRadius: 14,
		fontSize: 12.5,
		fontWeight: 600,
		textTransform: 'none',
		color: theme.palette.text.secondary,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		transition: theme.transitions.create(['background-color', 'color', 'border-color'], { duration: 150 }),
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	pillActive: {
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		borderColor: '#E8DC00',
		backgroundColor: '#FEF200',
		'&:hover': {
			backgroundColor: '#FFF53D'
		}
	},
	cardList: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1)
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: theme.spacing(5, 3, 5.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper
	},
	emptyIconWrap: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 54,
		height: 54,
		borderRadius: 16,
		marginBottom: theme.spacing(2),
		color: 'rgba(0,0,0,0.7)',
		backgroundColor: '#FEF200'
	},
	emptyIcon: {
		fontSize: 32
	},
	emptyTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5)
	},
	emptyHint: {
		color: theme.palette.text.secondary,
		maxWidth: 360,
		marginBottom: theme.spacing(3)
	},
	skelCard: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		padding: theme.spacing(1.5, 2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper
	},
	skelBody: {
		flex: 1
	},
	pagination: {
		marginTop: theme.spacing(1)
	},
	dangerName: {
		fontWeight: 700,
		color: theme.palette.text.primary
	},
	deleteConfirmBtn: {
		color: '#fff',
		backgroundColor: theme.palette.error.main,
		fontWeight: 700,
		'&:hover': {
			backgroundColor: theme.palette.error.dark
		}
	}
}));

const ROWS_PER_PAGE = 20;
const STATUS_FILTERS = ['all', 'draft', 'active', 'upcoming', 'expired'];

export default function PromoList() {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const { enqueueSnackbar } = useSnackbar();

	const singleton = PersistantData.getInstance();
	var { translation: language } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const [list, setList] = useState([]);
	const [brands, setBrands] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [brandFilter, setBrandFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [monthFilter, setMonthFilter] = useState('all');
	const [page, setPage] = useState(0);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editId, setEditId] = useState(null);
	const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		if (JString(gState.client).isValid()) {
			loadList(true);
			loadBrands();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gState.client]);

	const loadList = (initial = false) => {
		if (initial) {
			setLoading(true);
		} else {
			setRefreshing(true);
		}
		API_Service.get(req_promo_campaign, axiosDefaultConfig(gState.client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (parseInt(result) === 1) {
					setList(Array.isArray(extraData) ? extraData : []);
				} else {
					setList([]);
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }))
			.finally(() => {
				setLoading(false);
				setRefreshing(false);
			});
	};

	const loadBrands = () => {
		API_Service.get(req_stock_brand, axiosDefaultConfig(gState.client))
			.then(({ data }) => {
				const active = Array.isArray(data) ? data.filter((b) => parseInt(b.brand_status) === 1) : [];
				setBrands(active);
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }));
	};

	const monthOptions = useMemo(() => buildMonthOptions(), []);

	const filtered = useMemo(() => {
		return list.filter((row) => {
			if (brandFilter !== 'all' && String(row.brand_id) !== String(brandFilter)) {
				return false;
			}
			const status = getCampaignStatus(row);
			if (statusFilter !== 'all' && status !== statusFilter) {
				return false;
			}
			if (monthFilter !== 'all' && status !== 'draft' && !monthOverlaps(row.start_date, row.end_date, monthFilter)) {
				return false;
			}
			return true;
		});
	}, [list, brandFilter, statusFilter, monthFilter]);

	const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
	const safePage = page >= pageCount ? 0 : page;
	const paged = filtered.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

	const statusLabels = {
		all: language.all || 'All',
		draft: language.draft || 'Draft',
		active: language.active || 'Active',
		upcoming: language.upcoming || 'Upcoming',
		expired: language.expired || 'Expired'
	};

	const onBrandFilter = (e) => {
		setBrandFilter(e.target.value);
		setPage(0);
	};

	const onMonthFilter = (e) => {
		setMonthFilter(e.target.value);
		setPage(0);
	};

	const onStatusFilter = (value) => {
		setStatusFilter(value);
		setPage(0);
	};

	const openAdd = () => {
		setEditId(null);
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditId(row.id);
		setDialogOpen(true);
	};

	const onDialogClose = (didChange) => {
		setDialogOpen(false);
		if (didChange) {
			loadList(false);
		}
	};

	const onAskDelete = (row) => setConfirm({ open: true, id: row.id, name: row.name });

	const closeConfirm = () => {
		if (deleting) {
			return;
		}
		setConfirm({ open: false, id: null, name: '' });
	};

	const doDelete = () => {
		setDeleting(true);
		const params = axiosURIencode({ id: confirm.id });
		API_Service.put(req_promo_campaign_delete, params, axiosPutConfig(gState.client))
			.then(({ data }) => {
				const { result, message } = data;
				if (parseInt(result) === 1) {
					enqueueSnackbar(message || language.update_success, { variant: 'success' });
					setConfirm({ open: false, id: null, name: '' });
					loadList(false);
				} else {
					enqueueSnackbar(message || language.update_fail, { variant: 'error' });
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }))
			.finally(() => setDeleting(false));
	};

	const renderSkeleton = () => (
		<div className={classes.cardList}>
			{[0, 1, 2, 3, 4].map((i) => (
				<div key={i} className={classes.skelCard}>
					<Skeleton animation="wave" variant="rect" width={40} height={40} />
					<div className={classes.skelBody}>
						<Skeleton animation="wave" variant="text" width={`${40 - i * 3}%`} height={22} />
						<Skeleton animation="wave" variant="text" width="60%" height={16} />
					</div>
					<Skeleton animation="wave" variant="rect" width={72} height={24} />
				</div>
			))}
		</div>
	);

	const renderEmpty = (filtering) => (
		<div className={classes.empty}>
			<span className={classes.emptyIconWrap}>
				<LocalOfferOutlinedIcon className={classes.emptyIcon} />
			</span>
			<Typography variant="h6" className={classes.emptyTitle}>
				{filtering ? (language.no_data || 'No data found') : (language.no_promotions || 'No promotions yet')}
			</Typography>
			{!filtering && (
				<Typography variant="body2" className={classes.emptyHint}>
					{language.promotions_empty_hint}
				</Typography>
			)}
			{!filtering && (
				<Button
					variant="contained"
					disableElevation
					className={classes.newBtn}
					startIcon={<AddRoundedIcon />}
					onClick={openAdd}>
					{language.new_promotion}
				</Button>
			)}
		</div>
	);

	return (
		<div className={classes.root}>
			<div className={classes.toolbar}>
				<div className={classes.titleWrap}>
					<Typography variant="h5" className={classes.title}>
						{language.promo_list}
					</Typography>
					{!loading && list.length > 0 && (
						<span className={classes.countBadge}>{filtered.length}</span>
					)}
				</div>
				<TextField
					select
					className={classes.filterSelect}
					variant="outlined"
					size="small"
					label={language.brand || 'Brand'}
					value={brandFilter}
					onChange={onBrandFilter}>
					<MenuItem value="all">{language.all_brands}</MenuItem>
					{brands.map((b) => (
						<MenuItem key={b.brand_id} value={String(b.brand_id)}>{b.brand_name}</MenuItem>
					))}
				</TextField>
				<TextField
					select
					className={classes.filterSelect}
					variant="outlined"
					size="small"
					label={language.month || 'Month'}
					value={monthFilter}
					onChange={onMonthFilter}>
					<MenuItem value="all">{language.all_months}</MenuItem>
					{monthOptions.map((m) => (
						<MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
					))}
				</TextField>
				<Button
					variant="contained"
					disableElevation
					className={classes.newBtn}
					startIcon={<AddRoundedIcon />}
					onClick={openAdd}>
					{language.new_promotion}
				</Button>
			</div>

			<div className={classes.pillRow}>
				{STATUS_FILTERS.map((s) => (
					<Button
						key={s}
						disableElevation
						className={clsx(classes.pill, statusFilter === s && classes.pillActive)}
						onClick={() => onStatusFilter(s)}>
						{statusLabels[s]}
					</Button>
				))}
			</div>

			{refreshing && <LinearLoading />}

			{loading ? (
				renderSkeleton()
			) : filtered.length === 0 ? (
				renderEmpty(list.length > 0)
			) : (
				<Fade in timeout={220}>
					<div>
						<div className={classes.cardList}>
							{paged.map((row, idx) => (
								<PromoCard
									key={row.id}
									row={row}
									language={language}
									index={idx}
									onEdit={openEdit}
									onDelete={onAskDelete}
								/>
							))}
						</div>
						{filtered.length > ROWS_PER_PAGE && (
							<TablePagination
								className={classes.pagination}
								component="div"
								count={filtered.length}
								page={safePage}
								onChangePage={(e, p) => setPage(p)}
								rowsPerPage={ROWS_PER_PAGE}
								rowsPerPageOptions={[ROWS_PER_PAGE]}
							/>
						)}
					</div>
				</Fade>
			)}

			<PromoFormDialog
				open={dialogOpen}
				campaignId={editId}
				client={gState.client}
				language={language}
				onSnack={(msg, variant = 'success') => enqueueSnackbar(msg, { variant })}
				onClose={onDialogClose}
			/>

			<Dialog
				open={confirm.open}
				onClose={closeConfirm}
				TransitionComponent={GrowTransition}
				maxWidth="xs">
				<DialogTitle>{language.confirmation}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{language.delete_promotion_confirm}
					</DialogContentText>
					{confirm.name ? (
						<Typography variant="body2" className={classes.dangerName}>{confirm.name}</Typography>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeConfirm} color="inherit" disabled={deleting}>
						{language.cancel}
					</Button>
					<Button
						onClick={doDelete}
						className={classes.deleteConfirmBtn}
						variant="contained"
						disableElevation
						disabled={deleting}>
						{language.delete}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
